import React from 'react';
import { Row, Col, Table } from 'react-bootstrap';
import moment from 'moment';
import { getDay } from 'date-fns';

//components
import { ProjectsWidget } from '../../../../components/GenericForm/Widgets/ProjectsWidget';
import { CurrentUserGroupsWidget } from '../../../../components/GenericForm/Widgets/UserGroupWidget';
import CustomMultiSelect from '../../../../elements/CustomMultiSelect';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import { Textarea } from '../../../../elements/Input';
import SwitchWidget from '../../ControlPanel/SwitchWidget.js';

export const StepOne = ({
    t,
    dateFormat,
    resources,
    projectId,
    onProjectChange,
    groupId,
    groupMemberIds,
    onGroupChange,
    memberIds,
    onMembersChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    reason,
    onReasonChange,
    totalDays,
}) => {
    const allMembers = resources.map(m => ({ value: m.id, label: m.name }));
    const members = groupId ? allMembers.filter(m => groupMemberIds.includes(m.value)) : allMembers;

    return (
        <div className="hold-project-step-body">
            <Row className="mb-3">
                <Col md={6}>
                    <label className="fw-600 fs13 d-block mb-1">{t('Select Project')}</label>
                    <ProjectsWidget
                        value={projectId}
                        onChange={onProjectChange}
                        applyOnFirstLoad={false}
                    />
                </Col>
                <Col md={6}>
                    <label className="fw-600 fs13 d-block mb-1">
                        {t('Select Group')}
                    </label>
                    <CurrentUserGroupsWidget
                        user_id={0}
                        value={groupId}
                        onChange={onGroupChange}
                        isMember={false}
                    />
                    {!!groupId && (
                        <div className="fs11 text-muted mt-1">
                            {t('Total employee(s) in the group')}: {groupMemberIds.length}
                        </div>
                    )}
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={12}>
                    <label className="fw-600 fs13 d-block mb-1">{t('Select Members')} <span className="text-danger">*</span></label>
                    <CustomMultiSelect
                        options={members}
                        value={memberIds}
                        onChange={onMembersChange}
                    />
                </Col>
            </Row>
            <Row className="mb-2">
                <Col md={6}>
                    <DatePicker
                        label={<span className="fw-600 fs13">{t('From Date')} <span className="text-danger">*</span></span>}
                        selected={fromDate}
                        onChange={onFromDateChange}
                        dateFormat={dateFormat}
                        filterDate={date => getDay(date) === 1}
                        bgGrey
                        isClearable
                    />
                </Col>
                <Col md={6}>
                    <DatePicker
                        label={<span className="fw-600 fs13">{t('To Date')} <span className="text-danger">*</span></span>}
                        selected={toDate}
                        onChange={onToDateChange}
                        dateFormat={dateFormat}
                        minDate={fromDate || undefined}
                        filterDate={date => getDay(date) === 0}
                        bgGrey
                        isClearable
                    />
                </Col>
            </Row>
            {!!totalDays && (
                <div className="border-radius-10 d-flex align-items-center ps-2 p-1 col-3 gap-3 fw-600" style={{ backgroundColor: '#adc0e9', border: '1px solid #2550AC' }}>
                    <i className="fa fa-calendar" />
                    {t('Total days')}: {totalDays}
                </div>
            )}
            <div className="mt-3">
                <Textarea
                    label={<span className="fw-600 fs13">{t('Reason for hold')} ({t('optional')})</span>}
                    rows={3}
                    className="h-auto"
                    value={reason}
                    onChange={onReasonChange}
                />
            </div>
        </div>
    );
};

export const StepTwo = ({ t, templates, conflictingMembers, hasVacationConflict }) => (
    <div className="hold-project-step-body">
        <div className="border-radius-10 d-flex align-items-center p-2 gap-3 fw-600 fs14" style={{ backgroundColor: '#efefba', border: '1px solid #99aa0d' }}>
            <i className="fa fa-info-circle" />
            {t('These roster templates are currently active for the selected members during the hold window. Their upcoming cycles will need to be moved forward once the hold is registered.')}
        </div>
        {!!conflictingMembers.length && (
            <div className="border-radius-10 d-flex align-items-center p-2 gap-3 fw-600 fs14 mt-2" style={{ backgroundColor: '#f8d7da', border: '1px solid #dc3545' }}>
                <i className="fa fa-exclamation-triangle" />
                {t('These employee(s) already have a vacation or absence registered during this period and will not be included in the hold')}: {conflictingMembers.map(m => m.name).join(', ')}
            </div>
        )}
        <div className="table-scroll">
            <Table className="table-sm fs12 table-container">
                <thead>
                    <tr>
                        <th className='text-start'>{t('Template')}</th>
                        <th className='text-start'>{t('Members in this hold')}</th>
                        <th className='text-start'>{t('Shared With Others')}</th>
                    </tr>
                </thead>
                <tbody>
                    {templates.map(tpl => (
                        <tr key={tpl.template_id}>
                            <td className="fw-600 text-start">{tpl.template_name}</td>
                            <td className='text-start'>
                                {tpl.members.map((member, idx) => (
                                    <span key={member.id}>
                                        {idx > 0 && ', '}
                                        {member.name}
                                        {hasVacationConflict(member.id) && (
                                            <i
                                                className="fa fa-exclamation-triangle text-warning ms-1"
                                                title={t('This employee already has a vacation or absence registered during this period and will not be included in the hold')}
                                            />
                                        )}
                                    </span>
                                ))}
                            </td>
                            <td className='text-start'>
                                {tpl.shared
                                    ? <span className="hold-project-badge hold-project-badge-fork">{t('Yes')} ({tpl.other_assignees} {t('others')})</span>
                                    : <span className="hold-project-badge hold-project-badge-update">{t('No')}</span>}
                            </td>
                        </tr>
                    ))}
                    {!templates.length && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">{t('No affected templates found')}</td></tr>
                    )}
                </tbody>
            </Table>
        </div>
    </div>
);

export const StepThree = ({ t, dateFormat, assignments, affectedWorkPatterns, resources, fromDate: _fromDate, toDate: _toDate, totalDays }) => {
    const fromDate = moment(_fromDate).format(dateFormat);
    const toDate = moment(_toDate).format(dateFormat);
    const staggeredStart = moment(_toDate).add(1, 'day');
    const staggeredStartDate = staggeredStart.format(dateFormat);
    const staggeredEndDate = staggeredStart.clone().add(totalDays - 1, 'days').format(dateFormat);

    return (
        <div className="hold-project-step-body">
            <div className="border-radius-10 d-flex align-items-center p-2 gap-3 fw-600 fs14" style={{ backgroundColor: '#efefba', border: '1px solid #99aa0d' }}>
                <i className="fa fa-info-circle" />
                {t('Templates shared with employees outside this hold will get a new assignment version for the selected members only. Others will simply be updated in place.')}
            </div>
            <div className="table-scroll">
                <Table className="table-sm fs12 table-container">
                    <thead>
                        <tr className='w-100'>
                            <th className='text-start' style={{ width: '15%' }}>{t('Employee')}</th>
                            <th className='text-start' style={{ width: '50%' }}>{t('Template')}</th>
                            <th className='text-start d-flex align-items-center'>
                                <div>
                                    {t('Current')}
                                    <br />
                                    {t('plan')}
                                </div>
                                <div className="ms-2 me-2">&rarr;</div>
                                <div>
                                    {t('Staggered')}
                                    <br />
                                    {t('plan')}
                                </div>
                            </th>
                            <th className='text-start' style={{ width: '10%' }}>{t('Action')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {assignments.map(a => (
                            <tr key={`${a.template_id}-${a.id}`}>
                                <td className="fw-600 text-start">
                                    {a.name}
                                    {a.hasVacationConflict && (
                                        <i
                                            className="fa fa-exclamation-triangle text-warning ms-1"
                                            title={t('This employee already has a vacation or absence registered during this period and will not be included in the hold')}
                                        />
                                    )}
                                </td>
                                <td className='text-start'>{a.template_name}</td>
                                <td className="text-muted text-start d-flex align-items-center">
                                    <div>
                                        {fromDate}
                                        <br />
                                        {toDate}
                                    </div>
                                    <span className="ms-2 me-2">&rarr;</span>
                                    <div>
                                        {staggeredStartDate}
                                        <br />
                                        {staggeredEndDate}
                                    </div>
                                </td>
                                <td className='text-start'>
                                    {a.shared
                                        ? <span className="hold-project-badge hold-project-badge-fork">{t('New assignment version')}</span>
                                        : <span className="hold-project-badge hold-project-badge-update">{t('Update assignment')}</span>}
                                </td>
                            </tr>
                        ))}
                        {!assignments.length && (
                            <tr><td colSpan={5} className="text-center text-muted py-3">{t('No affected assignments found')}</td></tr>
                        )}
                    </tbody>
                </Table>
            </div>
            {!!affectedWorkPatterns.length && (
                <div className="mt-3">
                    <div className="border-radius-10 d-flex align-items-center p-2 gap-3 fw-600 fs14" style={{ backgroundColor: '#efefba', border: '1px solid #99aa0d' }}>
                        <i className="fa fa-info-circle" />
                        {t('These employees are on a recurring Work Pattern Assignment. The assignment itself will need to be adjusted for the hold - this will be worked out on the next (Confirm) step.')}
                    </div>
                    <div className="table-scroll">
                        <Table className="table-sm fs12 table-container">
                            <thead>
                                <tr>
                                    <th className='text-start'>{t('Employee')}</th>
                                    <th className='text-start'>{t('Work Pattern')}</th>
                                    <th className='text-start'>{t('Assignment Period')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {affectedWorkPatterns.map(wpa => (
                                    <tr key={wpa.work_pattern_assignment_id}>
                                        <td className="fw-600 text-start">{resources.find(r => r.id === wpa.user_id)?.name || wpa.user_id}</td>
                                        <td className='text-start'>{wpa.template_name}</td>
                                        <td className="text-muted text-start">
                                            {moment(wpa.start_date).format(dateFormat)}
                                            <span className="hold-project-cycle-arrow">&rarr;</span>
                                            {wpa.end_date ? moment(wpa.end_date).format(dateFormat) : t('Ongoing')}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
};

export const StepFour = ({ t, notifyUser, onNotifyUserChange, conflictingMembers, summaryRows }) => (
    <div className="hold-project-step-body">
        {!!conflictingMembers.length && (
            <div className="border-radius-10 d-flex align-items-center p-2 gap-3 fw-600 fs14 mb-2" style={{ backgroundColor: '#f8d7da', border: '1px solid #dc3545' }}>
                <i className="fa fa-exclamation-triangle" />
                {t('These employee(s) already have a vacation or absence registered during this period and will not be included in the hold')}: {conflictingMembers.map(m => m.name).join(', ')}
            </div>
        )}
        <div className="table-scroll">
            <Table className="table-sm fs12 table-container">
                <tbody>
                    {summaryRows.map(row => (
                        <tr key={row.label}>
                            <th className="text-start" style={{ width: '30%' }}>{row.label}</th>
                            <td className="text-start">{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
        <div className="border-radius-10 d-flex align-items-center p-2 gap-3 fw-600 fs14 mt-3" style={{ backgroundColor: '#efefba', border: '1px solid #99aa0d' }}>
            <i className="fa fa-lock" />
            {t('This hold will be stored in the Paused / Hold Projects register and can be reverted at any time by an Admin or PM.')}
        </div>
        <Row className="mt-2">
            <Col className="mt-1" sm={1}>
                <SwitchWidget
                    value={notifyUser}
                    onChange={onNotifyUserChange} />
            </Col>
            <Col sm={11}>
                <label className="control-label">{t('Notify User')}</label>
            </Col>
        </Row>
    </div>
);
